import {memo} from 'react';
import type {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import FeatureIconCard from '@/components/shared/cards/FeatureIconCard';
import IconListCard from '@/components/shared/cards/IconListCard';

export interface StepCard {
    icon?: ReactNode;
    title: string;
    /** Accent line under the title, e.g. "Delivered in 24–48 Hours". */
    subtitle?: string;
    description: string;
}

export interface ListCard {
    title: string;
    items: string[];
    icon?: ReactNode;
}

export interface StepCardsWithListsProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    /** The stages, three to a row. */
    steps: StepCard[];
    /** Supporting lists below the steps, two to a row. */
    lists?: ListCard[];
}

const gridItemSx = {display: 'flex'} as const;

// The lists sit as a centred pair rather than on the 12-column grid: at half the
// container each they ended up marooned at opposite edges with a wide dead gap
// between them. Capped and centred, they read as one block under the steps.
const listsWrapSx = {
    mt: {xs: 4, md: 6},
    display: 'flex',
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
    gap: 3
} as const;

// Half the row each, so the pair fills the container instead of sitting narrow and
// centred with dead space around them. Each card is then wide enough for a single
// column of items to run on one line — see the note on `columns` below.
const listItemSx = {display: 'flex', flex: {xs: '1 1 100%', md: '1 1 0'}} as const;

/**
 * StepCardsWithLists — a stage-by-stage explainer: icon-led step cards across the
 * top, then the supporting lists that qualify them ("what to bring", "what drives
 * the number").
 *
 * Both card types are the shared `FeatureIconCard` / `IconListCard`, so this
 * section owns layout only.
 */
const StepCardsWithLists = ({eyebrow, heading, subheading, steps, lists}: StepCardsWithListsProps) => (
    <MUIContainer>
        <SectionHeader eyebrow={eyebrow} heading={heading} subheading={subheading} />

        <Grid container spacing={3}>
            {steps.map((step) => (
                <Grid item xs={12} sm={6} md={4} key={step.title} sx={gridItemSx}>
                    <FeatureIconCard
                        icon={step.icon}
                        title={step.title}
                        subtitle={step.subtitle}
                        description={step.description}
                        iconPlacement="stacked"
                        iconTone="light"
                        hoverFill
                    />
                </Grid>
            ))}
        </Grid>

        {lists && lists.length > 0 && (
            <Box sx={listsWrapSx}>
                {lists.map((list) => (
                    <Box key={list.title} sx={listItemSx}>
                        <IconListCard title={list.title} items={list.items} icon={list.icon} columns={2} />
                    </Box>
                ))}
            </Box>
        )}
    </MUIContainer>
);

export default memo(StepCardsWithLists);
